import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useCollect, useRequest } from 'hooks';

import Show from 'components/Show';
import Alert from 'components/Misc/Alert';
import { Title } from 'components/Misc/Typography';
import { Form, Input, Select, Duration, Button } from 'components/Misc/Form';

import { useAppStore } from 'store/useAppStore';
import { useExamCreateStore } from 'store/exam/useExamCreateStore';

const Create = () => {
	// Use router's history
	const history = useHistory();

	// User app's store
	const role = useAppStore(state => state.user.role);

	// Use exam's creation store
	const [exam, onChange, onReset] = useExamCreateStore(state => [
		state.exam,
		state.onChange,
		state.onReset
	]);

	// Store subject request parameters
	const subjectParameters = useMemo(() => (['admin', 'employee'].includes(role) ? {} : {
		condition: 'related'
	}), [role]);

	// Store course request parameters
	const courseParameters = useMemo(() => (['admin', 'employee'].includes(role) ? {
		subjectId: exam.subjectId
	} : {
		condition: 'related',
		subjectId: exam.subjectId
	}), [role, exam.subjectId]);

	// Store request parameters
	const examParameters = useMemo(() => ({
		eager: 'subject',
		courseId: exam.courseId,
		'status:neq': 'archived',
	}), [exam.courseId]);

	// Store collected subjects
	const [[subjects], collectSubjects] = useCollect('subjects', subjectParameters);

	// Store collected courses
	const [[courses], collectCourses] = useCollect('courses', courseParameters);

	// Store collected exam
	const [[existing], collectExisting] = useCollect('exams', examParameters);

	// Store request
	const [[progress], request] = useRequest('post', 'exams');

	// Collect subjects on mount
	useEffect(() => {
		(async () => await collectSubjects())();
	}, [collectSubjects]);

	// Collect courses on subject selection
	useEffect(() => {
		if (courseParameters.subjectId) (async () => await collectCourses())();
	}, [courseParameters, collectCourses]);

	// Collect existing exam on course selection
	useEffect(() => {
		if (examParameters.courseId) (async () => await collectExisting())();
	}, [examParameters, collectExisting]);

	// Handle store cleanup
	useEffect(() => {
		return () => onReset();
	}, []);

	// Handle submit event
	const onSubmit = async (event) => {
		event.preventDefault();

		// TODO: Remove after test
		console.log(exam);

		// Try to create exam
		const [success, error] = await request(exam);

		// Redirect on successful creation
		if (success) return history.push('/exams');

		// TODO: Handle errors
		if (error) console.log(error);
	};

	// TODO: Remove after testing
	useEffect(() => console.log('EXAM:CREATE:', exam), [exam]);

	return (
		<Form
			onSubmit={onSubmit}
			className="mx-auto p-4 max-w-screen-md grid md:grid-cols-2 gap-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors"
		>
			<Title
				title="Pamatinformācija"
				className="md:col-span-2 text-xl"
			/>

			<Input
				type="text"
				name="title"
				label="Nosaukums"
				value={exam.title}
				onChange={onChange}
			/>

			<Input
				type="text"
				name="titlePublic"
				label="Publiskais nosaukums"
				value={exam.titlePublic}
				onChange={onChange}
			/>

			<Select
				name="subjectId"
				label="Mācību kurss"
				icon="bookmark_border"
				placeholder=" "
				value={exam.subjectId}
				onChange={onChange}
				options={subjects?.map(subject => ({
					value: subject.id,
					name: subject.title
				}))}
			/>

			<Select
				name="courseId"
				label="Mācību modulis"
				icon="book"
				placeholder=" "
				disabled={!courses?.length}
				value={exam.courseId}
				onChange={onChange}
				options={courses?.map(course => ({
					value: course.id,
					name: course.title
				}))}
			/>

			<div>
				<Duration
					name="duration"
					label="Izpildes laiks"
					value={exam.duration}
					onChange={onChange}
					className="max-w-[150px]"
				/>
			</div>
			
			{/* Spacer for grid */}
			<div className="hidden md:block" />

			{/* Show existing exam error */}
			<Show if={existing?.length}>
				<Alert
					theme="danger"
					icon="warning"
					message="Izvēlētajam modulim jau ir piesaistīts pārbaudes darbs! Lai modulim pievienotu jaunu pārbaudes darbu ir jāaizdzēš esošais!"
				/>

				<div className="py-2 px-3 flex items-center space-x-2 border border-neutral-200 dark:border-neutral-700 rounded-lg transition-colors">
					<Title
						title={existing?.[0]?.title}
						subtitle={existing?.[0]?.subject.title}
						className="flex-1 whitespace-nowrap text-ellipsis overflow-hidden"
					/>

					<Button
						small
						square
						icon="link"
						to={`/exams/${existing?.[0]?.id}`}
					/>
				</div>
			</Show>

			<div className="md:col-span-2 flex justify-end">
				<Button
					submit
					icon="done"
					value="Izveidot"
					theme="success"
					progress={progress}
				/>
			</div>
		</Form>
	);
};

export default Create;